import { ALL_ENTITLEMENT_IDS, productGrants, productTrialGrants } from './catalog';
import type { EntitlementId, EntitlementsMap, ProductId } from './types';

export function emptyEntitlementsMap(): EntitlementsMap {
  return ALL_ENTITLEMENT_IDS.reduce((acc, id) => {
    acc[id] = false;
    return acc;
  }, {} as EntitlementsMap);
}

export function normalizeEntitlementsMap(value: unknown): EntitlementsMap {
  const base = emptyEntitlementsMap();
  if (!value || typeof value !== 'object') {
    return base;
  }
  for (const id of ALL_ENTITLEMENT_IDS) {
    if (id in (value as Record<string, unknown>)) {
      base[id] = Boolean((value as Record<string, boolean>)[id]);
    }
  }
  return base;
}

export function hasEntitlement(
  map: EntitlementsMap | null | undefined,
  id: EntitlementId,
): boolean {
  return Boolean(map?.[id]);
}

export function hasAnyEntitlement(
  map: EntitlementsMap | null | undefined,
  ids: EntitlementId[],
): boolean {
  return ids.some((id) => hasEntitlement(map, id));
}

export function hasAllEntitlements(
  map: EntitlementsMap | null | undefined,
  ids: EntitlementId[],
): boolean {
  return ids.every((id) => hasEntitlement(map, id));
}

/** True when everything the product grants is already held. */
export function ownsProduct(
  map: EntitlementsMap | null | undefined,
  productId: ProductId,
): boolean {
  return hasAllEntitlements(map, productGrants(productId));
}

/**
 * True when the map holds the trial slice but not the full product, i.e. the
 * member is mid-trial and the rest unlocks on the first charge.
 */
export function ownsTrialSliceOnly(
  map: EntitlementsMap | null | undefined,
  productId: ProductId,
): boolean {
  return hasAllEntitlements(map, productTrialGrants(productId)) && !ownsProduct(map, productId);
}

export const TOTAL_PROGRAM_SESSIONS = 12;

export function lessonEntitlementId(sessionNumber: number): EntitlementId {
  return `lesson_${sessionNumber}` as EntitlementId;
}

/** Lesson session numbers the buyer has paid for, ascending. */
export function entitledSessionNumbers(map: EntitlementsMap | null | undefined): number[] {
  const sessions: number[] = [];
  for (let n = 1; n <= TOTAL_PROGRAM_SESSIONS; n += 1) {
    if (hasEntitlement(map, lessonEntitlementId(n))) {
      sessions.push(n);
    }
  }
  return sessions;
}

export function hasAnyLessonEntitlement(map: EntitlementsMap | null | undefined): boolean {
  return entitledSessionNumbers(map).length > 0;
}
